<script lang="ts">
  import { page } from '$app/stores';

  import { DirectusImage, Preset } from '@do/svelte-directus-images';
  import { useWallet } from '@do/svelte-web3-manager';

  import { Routes } from '@shared/configs';
  import { Avatar } from '@shared/ui/avatar';
  import { BurgerButton } from '@shared/ui/buttons/burger';
  import { ThemeToggle } from '@shared/ui/theme';
  import { cn, quidget } from '@shared/utils';
  import { formatWalletAddress } from '@shared/utils';
  import { is } from '@shared/utils';

  import { mobileMenuActivity } from '@features/mobile-menu/models';
  import { Navigation } from '@features/navigation';

  import { Search } from '@widgets/search';
  import { walletSidebarModel } from '@widgets/wallet-sidebar';

  import { getLogoFontSizeStyles } from '../libs';

  const wallet = useWallet();

  let offset = 0;
  let y = 0;
  let lastY = 0;
  let innerWidth = 0;
  let isShowHeader = true;

  function handleScroll(y: number) {
    if (y < 80 || $mobileMenuActivity) {
      // do not hide when mobile menu is open (bg page still can be scrolled in safari)
      isShowHeader = true;

      return;
    }

    const dy = lastY - y;

    lastY = y;

    const shouldShowHeader = !(y < offset || dy < 0);

    if (shouldShowHeader !== isShowHeader) {
      isShowHeader = shouldShowHeader;
    }
  }

  $: handleScroll(y);
  $: institutionLogo = $page.data.configurations.logo;
  $: isMainPage = $page.route.id === '/';
  $: href = isMainPage ? undefined : '/';

  $: {
    if ($mobileMenuActivity) {
      quidget.hide();
    } else {
      quidget.show();
    }
  }
</script>

<header
  class="bg-default-50 dark:bg-store-dark sticky top-0 z-20 w-auto transition-transform duration-1000 {is(
    isShowHeader,
    'translate-y-0',
    '-translate-y-[125%]',
  )}"
>
  <div class="4xl:px-12 mx-auto px-5 text-2xl md:px-8 xl:px-12">
    <div
      class="{cn('flex h-[64px] items-center gap-5 md:h-[72px] lg:gap-10 xl:gap-5', {
        'lg:grid lg:grid-cols-[1fr_auto_1fr]': $page.route.id !== Routes.ArtistWalletAuthorization,
      })}"
    >
      {#if $page.route.id !== Routes.ArtistWalletAuthorization}
        <nav class="hidden lg:order-2 lg:items-center lg:gap-20 xl:flex">
          <Navigation />
        </nav>

        <div class="xl:hidden">
          <BurgerButton on:click="{mobileMenuActivity.toggle}" isActive="{$mobileMenuActivity}" />
        </div>
      {/if}

      <svelte:element
        this="{isMainPage ? 'div' : 'a'}"
        {href}
        class="{cn('block h-[28px] w-full content-center', {
          'h-full py-2': !institutionLogo,
        })}"
      >
        {#if institutionLogo}
          <DirectusImage
            imageData="{institutionLogo}"
            alt="{institutionLogo.title}"
            imgClasses="w-auto h-full object-contain dark:invert"
            attributes="{{
              draggable: false,
            }}"
            pictureSizes="{{ default: '200px' }}"
            presetKeys="{Preset.Thumbnail}"
          />
        {:else}
          {@const institutionName = $page.data.configurations.name}
          {@const fontSizeStyles = getLogoFontSizeStyles(institutionName)}

          <span class="{fontSizeStyles} block capitalize">{institutionName}</span>
        {/if}
      </svelte:element>

      <div class="flex flex-shrink-0 items-center gap-5 lg:order-3 lg:justify-end">
        <div class="{cn('md:flex', { hidden: $page.route.id !== Routes.ArtistWalletAuthorization })}">
          <ThemeToggle />
        </div>

        <Search />

        {#if $page.route.id !== Routes.ArtistWalletAuthorization}
          <button
            on:click="{() => walletSidebarModel.walletSidebarActivity.toggle()}"
            class="flex text-[15px] font-medium md:items-center md:gap-3"
          >
            {#if $wallet}
              <p class="hidden md:block">{formatWalletAddress($wallet.address)}</p>

              <Avatar wallet="{$wallet.address}" />
            {:else}
              Connect wallet
            {/if}
          </button>
        {/if}
      </div>
    </div>
  </div>
</header>

<svelte:window bind:scrollY="{y}" bind:innerWidth />
